import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchMaintenances,
    fetchMaintenanceById,
    fetchMaintenancesByVehicleId,
    createMaintenance,
    updateMaintenance,
    deleteMaintenance,
    fetchMaintenanceEmployees,
    assignEmployeeToMaintenance,
    removeEmployeeFromMaintenance,
} from "../../api/maintenance/maintenanceService";

export const useMaintenances = (vehId = null) => {
    const queryClient = useQueryClient();
    const queryKey = vehId ? ["maintenances", "byVehicle", vehId] : ["maintenances"];
    const queryFn = vehId ? () => fetchMaintenancesByVehicleId(vehId) : fetchMaintenances;

    const {
        data: maintenances = [],
        error,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: queryKey,
        queryFn: queryFn,
        enabled: true,
        staleTime: 1000 * 60 * 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const invalidateMaintenancesCache = () => {
        queryClient.invalidateQueries({ queryKey: ["maintenances"] });
        if (vehId) {
            queryClient.invalidateQueries({ queryKey: ["maintenances", "byVehicle", vehId] });
        }
    };

    // --- Mutaciones CRUD para MANTENIMIENTOS ---
    const createMutation = useMutation({
        mutationFn: createMaintenance,
        onSuccess: (newMaintenanceData) => {
            invalidateMaintenancesCache();
            queryClient.setQueryData(["maintenance", newMaintenanceData.manid], newMaintenanceData);
            queryClient.refetchQueries({ queryKey: ["maintenance", newMaintenanceData.manid], exact: true });
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", newMaintenanceData.manid] });
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateMaintenance,
        onSuccess: (updatedMaintenanceData) => {
            invalidateMaintenancesCache();
            queryClient.setQueryData(["maintenance", updatedMaintenanceData.manid], updatedMaintenanceData);
            queryClient.refetchQueries({ queryKey: ["maintenance", updatedMaintenanceData.manid], exact: true });
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", updatedMaintenanceData.manid] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteMaintenance,
        onSuccess: (_, variables) => {
            invalidateMaintenancesCache();
            queryClient.invalidateQueries({ queryKey: ["maintenanceEmployees", variables.manId] });
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", variables.manId] });
        }
    });

    return {
        maintenances,
        isLoading,
        isFetching,
        error,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};

export const useMaintenanceEmployees = (manId) => {
    const queryClient = useQueryClient();

    const {
        data: assignedEmployees = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ["maintenanceEmployees", manId],
        queryFn: () => fetchMaintenanceEmployees(manId),
        enabled: !!manId,
        staleTime: 1000 * 30, // 30 segundos
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const assignEmployeeMutation = useMutation({
        mutationFn: assignEmployeeToMaintenance,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["maintenanceEmployees", variables.manId] });
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", variables.manId] });
        },
    });

    const removeEmployeeMutation = useMutation({
        mutationFn: removeEmployeeFromMaintenance,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["maintenanceEmployees", variables.manId] });
            queryClient.invalidateQueries({ queryKey: ["maintenanceHistory", variables.manId] });
        },
    });

    return {
        assignedEmployees,
        isLoading,
        error,
        assignEmployeeMutation,
        removeEmployeeMutation,
    };
};

export const useMaintenanceById = (manId) => {
    return useQuery({
        queryKey: ['maintenance', manId],
        queryFn: () => fetchMaintenanceById(manId),
        enabled: Number.isInteger(manId) && manId > 0,
        staleTime: 1000 * 60 * 5,
    });
};